import { useMemo, useState } from "react";
import { FileUp, Pencil, Plus, Tags, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { LoadingState } from "@/components/ui/loading-state";
import { EmptyState } from "@/components/ui/empty-state";
import { MetaRate } from "@/models/pricing";
import { formatters } from "@/utils/formatters";
import { countryName } from "@/pages/organization-detail/org-kpis.meta";
import { useMetaRates } from "./hooks/useMetaRates";
import { useRemoveMetaRate } from "./hooks/useRemoveMetaRate";
import { RateFormDialog } from "./components/RateFormDialog";
import { ImportRatesDialog } from "./components/ImportRatesDialog";

/** The default row sorts first: "" comes before every country code. */
const sortKey = (country: string) => (country === "*" ? "" : country);

/** Rates are fractions of a cent apart; four decimals keeps them distinct. */
const formatRate = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 4,
    maximumFractionDigits: 4
  }).format(value);

const PricingPage = () => {
  const { data: rates, isLoading, error, refetch } = useMetaRates();
  const { mutate: removeRate } = useRemoveMetaRate();
  const [editing, setEditing] = useState<MetaRate | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [showSuperseded, setShowSuperseded] = useState(false);

  const now = Date.now();
  // The default row first, then countries alphabetically; within a country,
  // newest first, with the one in force today marked.
  const rows = useMemo(() => {
    const sorted = [...(rates ?? [])].sort(
      (a, b) =>
        sortKey(a.country).localeCompare(sortKey(b.country)) ||
        new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime()
    );
    const current = new Set<string>();
    const seen = new Set<string>();
    for (const r of sorted) {
      if (!seen.has(r.country) && new Date(r.effectiveFrom).getTime() <= now) {
        current.add(r._id);
        seen.add(r.country);
      }
    }
    return sorted.map((r) => ({
      ...r,
      inForce: current.has(r._id),
      // Older than the row in force: kept to price history, not to be edited.
      superseded: !current.has(r._id) && new Date(r.effectiveFrom).getTime() <= now
    }));
  }, [rates, now]);
  const superseded = rows.filter((r) => r.superseded).length;
  const visibleRows = showSuperseded ? rows : rows.filter((r) => !r.superseded);

  const openForm = (rate: MetaRate | null) => {
    setEditing(rate);
    setIsFormOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Meta pricing"
        description="Meta's per-message rates, used to estimate each organization's WhatsApp cost. Service messages are priced at the utility rate unless a row says otherwise."
        action={
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2" onClick={() => setIsImportOpen(true)}>
              <FileUp className="h-4 w-4" />
              Import rate card
            </Button>
            <Button className="gap-2" onClick={() => openForm(null)}>
              <Plus className="h-4 w-4" />
              Add rate
            </Button>
          </div>
        }
      />

      <RateFormDialog open={isFormOpen} onOpenChange={setIsFormOpen} rate={editing} />
      <ImportRatesDialog open={isImportOpen} onOpenChange={setIsImportOpen} />

      {isLoading ? (
        <LoadingState isLoading={isLoading} loadingText="Loading rates..." />
      ) : error ? (
        <LoadingState error={error} errorTitle="Failed to load rates" onRetry={() => refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Tags}
          title="No rates yet"
          description="Import Meta's rate card or add the countries your organizations message. Until then every cost shows as $0."
          actionLabel="Add rate"
          onAction={() => openForm(null)}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            {superseded > 0 && (
              <div className="flex items-center justify-end gap-2 border-b px-4 py-3">
                <Switch
                  id="show-superseded"
                  checked={showSuperseded}
                  onCheckedChange={setShowSuperseded}
                />
                <Label htmlFor="show-superseded" className="text-sm font-normal">
                  Show earlier rates ({superseded}), still used to price past messages
                </Label>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Market</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead className="text-right">Marketing</TableHead>
                  <TableHead className="text-right">Utility</TableHead>
                  <TableHead className="text-right">Authentication</TableHead>
                  <TableHead className="text-right">Service</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleRows.map((rate) => (
                  <TableRow key={rate._id} className={rate.inForce ? "" : "text-muted-foreground"}>
                    <TableCell>
                      <div className="flex items-center gap-2 font-medium">
                        {countryName(rate.country)}
                        <span className="text-xs text-muted-foreground">{rate.country}</span>
                        {rate.inForce && <Badge variant="success">In force</Badge>}
                      </div>
                      {rate.label && (
                        <div className="text-xs text-muted-foreground">{rate.label}</div>
                      )}
                    </TableCell>
                    <TableCell>{formatters.formatDate(rate.effectiveFrom)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatRate(rate.marketing)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatRate(rate.utility)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatRate(rate.authentication)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {rate.service !== undefined ? (
                        formatRate(rate.service)
                      ) : (
                        <span className="text-muted-foreground">= utility</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Edit"
                          onClick={() => openForm(rate)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Remove"
                          onClick={() => removeRate(rate._id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PricingPage;
